import { isDevMode } from '@angular/core';
import { ApolloClient, InMemoryCache, split } from '@apollo/client/core';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { HttpLink } from 'apollo-angular/http';
import { Client, ClientOptions, createClient } from 'graphql-ws';
import Keycloak from 'keycloak-js';
import { AuthenticationService, AuthorizationService } from '~ngx-shared/authentication';
import { buildSocketHeaders } from './connection-params';
import { shouldRetrySocket, socketRetryWait } from './socket-retry';

// Apollo 4.2+ types results from these defaults; they must match defaultOptions below.
declare module '@apollo/client' {
  namespace ApolloClient {
    namespace DeclareDefaultOptions {
      interface WatchQuery {
        errorPolicy: 'ignore';
      }
      interface Query {
        errorPolicy: 'all';
      }
      interface Mutate {
        errorPolicy: 'all';
      }
    }
  }
}

interface RestartableClient extends Client {
  restart(): void;
}

function createRestartableClient(options: ClientOptions): RestartableClient {
  let restartRequested = false;
  let restart = () => {
    restartRequested = true;
  };

  const client = createClient({
    ...options,
    on: {
      ...options.on,
      opened: (socket: any) => {
        options.on?.opened?.(socket);

        restart = () => {
          if (socket.readyState === WebSocket.OPEN) {
            // if the socket is still open for the restart, do the restart
            socket.close(4205, 'Client Restart');
          } else {
            // otherwise the socket might've closed, indicate that you want
            // a restart on the next opened event
            restartRequested = true;
          }
        };

        // just in case you were eager to restart
        if (restartRequested) {
          restartRequested = false;
          restart();
        }
      }
    }
  });

  return {
    ...client,
    restart: () => restart()
  };
}

export function apolloOptionsFactory(
  httpLink: HttpLink,
  authenticationService: AuthenticationService,
  authorizationService: AuthorizationService,
  keycloak: Keycloak,
  hasuraUrl: string,
  hasuraWsUrl: string
): ApolloClient.Options {
  if (isDevMode()) {
    // Adds messages only in a dev environment
    loadDevMessages();
  }

  loadErrorMessages();

  // Create a http link:
  const http = httpLink.create({
    uri: hasuraUrl
  });

  const client = createRestartableClient({
    url: hasuraWsUrl,
    shouldRetry: shouldRetrySocket,
    retryAttempts: Infinity,
    retryWait: socketRetryWait,
    keepAlive: 30_000,
    connectionParams: async () => {
      // Hasura answers an expired token with a retryable 4403, so the client would loop.
      try {
        await keycloak.updateToken(30);
      } catch {
        // A failed refresh is usually the outage; only a missing token is unrecoverable.
        if (!keycloak.refreshToken) {
          await keycloak.login();
        }
      }

      return {
        headers: buildSocketHeaders(
          keycloak.token,
          authorizationService.authorizationStore?.value.currentRole,
          authenticationService.authStore.value?.['headers']
        )
      };
    }
  });
  const ws = new GraphQLWsLink(client);

  // Add a restart method to ws
  authorizationService.wsRestart = () => client.restart();

  // using the ability to split links, you can send data to each link
  // depending on what kind of operation is being sent
  const link = split(
    // split based on operation type
    ({ query }) => {
      const node = getMainDefinition(query);
      return node.kind === 'OperationDefinition' && node.operation === 'subscription';
    },
    ws,
    http
  );

  return {
    link,
    cache: new InMemoryCache({
      typePolicies: {}
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'ignore',
        notifyOnNetworkStatusChange: false
      },
      query: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      },
      mutate: {
        errorPolicy: 'all'
      }
    }
  };
}
